import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  Users,
  ArrowsLeftRight,
  CaretDown,
  Armchair,
} from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { api, trackRouteEvent } from "@/lib/api";

// Tiny window-matchMedia hook so we can swap to a CheapOair-style stacked
// layout on phones without breaking the desktop bar. Listens for resize so
// rotating an iPad doesn't lock the user to the wrong layout.
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

const TRIP_TYPES = [
  { v: "round_trip", l: "Round Trip" },
  { v: "one_way", l: "One Way" },
  // Multi-City is hidden until full multi-slice UX is built end-to-end.
  // { v: "multi_city", l: "Multi-City" },
];

const CABINS = [
  { v: "economy", l: "Economy" },
  { v: "premium", l: "Premium" },
  { v: "business", l: "Business" },
  { v: "first", l: "First" },
];

function AirportPicker({ value, onChange, label, defaults, cache, testid, align = "start" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolved, setResolved] = useState(null);
  const debRef = useRef(null);

  const sel = cache[value] || (defaults || []).find((a) => a.code === value) || resolved;

  useEffect(() => {
    if (!value) { setResolved(null); return; }
    if (cache[value] || (defaults || []).some((a) => a.code === value)) {
      setResolved(null);
      return;
    }
    let cancel = false;
    api
      .get(`/products/places/suggestions?q=${encodeURIComponent(value)}`)
      .then(({ data }) => {
        if (cancel) return;
        const hit = (data || []).find((a) => a.code === value) || (data || [])[0];
        if (hit) setResolved(hit);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [value, cache, defaults]);

  const list = query.trim() ? results : defaults;

  useEffect(() => {
    if (!open) return;
    if (debRef.current) clearTimeout(debRef.current);
    if (!query.trim()) { setResults([]); return; }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/products/places/suggestions?q=${encodeURIComponent(query.trim())}`,
        );
        setResults(data || []);
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [query, open]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="text-left w-full bg-transparent px-4 md:px-5 py-2.5 h-full flex flex-col justify-center hover:bg-white/5 transition-colors"
        >
          <span className="text-[10px] tracking-[0.22em] uppercase text-[#A09E9A] font-semibold leading-none mb-1.5">
            {label}
          </span>
          <span className="text-[#F7F5F0] text-sm font-medium truncate leading-tight">
            {sel ? (
              <>
                <span className="text-[#C29B62] font-semibold">{sel.code}</span>
                <span className="text-[#A09E9A]"> · </span>
                <span>{sel.city || sel.name}</span>
              </>
            ) : (
              value || "Select airport"
            )}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[340px] p-0 bg-[#141415] border-white/10 text-[#F7F5F0]"
        align={align}
      >
        <Command className="bg-[#141415]" shouldFilter={false}>
          <CommandInput
            placeholder="Search city, airport or IATA code..."
            className="text-[#F7F5F0]"
            value={query}
            onValueChange={setQuery}
            data-testid={`${testid}-search`}
          />
          <CommandList>
            <CommandEmpty>{loading ? "Searching..." : "No airport found."}</CommandEmpty>
            <CommandGroup heading={query.trim() ? "Results" : "Popular destinations"}>
              {(list || []).map((o, i) => (
                <CommandItem
                  key={`${o.code}-${i}`}
                  value={`${o.code}-${i}`}
                  onSelect={() => { onChange(o.code, o); setOpen(false); setQuery(""); }}
                  data-testid={`${testid}-opt-${o.code}`}
                  className="flex items-center justify-between cursor-pointer aria-selected:bg-[#C29B62]/15 text-[#F7F5F0]"
                >
                  <div className="min-w-0 pr-2">
                    <p className="text-sm font-medium truncate">
                      {o.city || o.name}
                      {o.country ? (
                        <span className="text-[#A09E9A] font-normal"> · {o.country}</span>
                      ) : null}
                    </p>
                    <p className="text-xs text-[#A09E9A] truncate">{o.name}</p>
                  </div>
                  <span className="text-[#C29B62] font-semibold text-sm shrink-0">{o.code}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function PaxCabinPopover({ pax, setPax, cabin, setCabin, variant = "chip" }) {
  const cabinLabel = CABINS.find((c) => c.v === cabin)?.l || "Economy";
  const total = (pax.adults || 0) + (pax.children || 0) + (pax.infants || 0);
  const update = (key, val) => setPax((p) => ({ ...p, [key]: val }));

  const PaxRow = ({ label, sub, value, onMinus, onPlus, minusDisabled, plusDisabled, testid }) => (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm text-[#F7F5F0] font-medium">{label}</p>
        <p className="text-[11px] text-[#A09E9A] mt-0.5">{sub}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={onMinus}
          disabled={minusDisabled}
          data-testid={`${testid}-decrement`}
          className="w-8 h-8 border border-white/15 text-[#F7F5F0] hover:border-[#C29B62] hover:text-[#C29B62] transition-colors flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-white/15 disabled:hover:text-[#F7F5F0]"
        >
          −
        </button>
        <span
          data-testid={`${testid}-value`}
          className="w-6 text-center text-[#F7F5F0] font-medium"
        >{value}</span>
        <button
          type="button"
          onClick={onPlus}
          disabled={plusDisabled}
          data-testid={`${testid}-increment`}
          className="w-8 h-8 border border-white/15 text-[#F7F5F0] hover:border-[#C29B62] hover:text-[#C29B62] transition-colors flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-white/15 disabled:hover:text-[#F7F5F0]"
        >
          +
        </button>
      </div>
    </div>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        {variant === "row" ? (
          // Mobile: full-width labelled card matching the From/To/Date rows
          <button
            type="button"
            data-testid="pax-cabin-trigger"
            className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
          >
            <div className="flex flex-col">
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#A09E9A] font-semibold leading-none mb-1.5">
                Travelers &amp; Cabin
              </span>
              <span className="text-[#F7F5F0] text-sm font-medium leading-tight flex items-center gap-1.5">
                <Users size={13} weight="duotone" className="text-[#C29B62]" />
                {total} {total === 1 ? "Traveler" : "Travelers"}
                <span className="text-white/20 mx-1">·</span>
                <Armchair size={13} weight="duotone" className="text-[#C29B62]" />
                {cabinLabel}
              </span>
            </div>
            <CaretDown size={13} className="text-[#A09E9A]" />
          </button>
        ) : (
          // Desktop: compact chip in the top utility row
          <button
            type="button"
            data-testid="pax-cabin-trigger"
            className="flex items-center gap-2 px-3 py-1.5 border border-white/10 text-[#F7F5F0] text-xs hover:border-[#C29B62]/60 hover:text-[#C29B62] transition-colors"
          >
            <Users size={14} weight="duotone" className="text-[#C29B62]" />
            <span className="font-medium">{total} {total === 1 ? "Traveler" : "Travelers"}</span>
            <span className="text-white/20">|</span>
            <Armchair size={14} weight="duotone" className="text-[#C29B62]" />
            <span className="font-medium">{cabinLabel}</span>
            <CaretDown size={11} className="text-[#A09E9A]" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] p-4 bg-[#141415] border-white/10 text-[#F7F5F0]"
        align={variant === "row" ? "center" : "end"}
      >
        <p className="tcp-label tcp-label-dark mb-2">Travelers</p>
        <div className="divide-y divide-white/5">
          <PaxRow
            label="Adults"
            sub="Aged 12 and over"
            value={pax.adults}
            onMinus={() => update("adults", Math.max(1, pax.adults - 1))}
            onPlus={() => update("adults", Math.min(9, pax.adults + 1))}
            minusDisabled={pax.adults <= 1 || pax.adults <= pax.infants}
            plusDisabled={pax.adults + pax.children + pax.infants >= 9}
            testid="pax-adults"
          />
          <PaxRow
            label="Children"
            sub="Aged 2 to 11"
            value={pax.children}
            onMinus={() => update("children", Math.max(0, pax.children - 1))}
            onPlus={() => update("children", Math.min(8, pax.children + 1))}
            minusDisabled={pax.children <= 0}
            plusDisabled={pax.adults + pax.children + pax.infants >= 9}
            testid="pax-children"
          />
          <PaxRow
            label="Infants"
            sub="Under 2 · seated on adult's lap"
            value={pax.infants}
            onMinus={() => update("infants", Math.max(0, pax.infants - 1))}
            onPlus={() => update("infants", Math.min(pax.adults, pax.infants + 1))}
            minusDisabled={pax.infants <= 0}
            plusDisabled={pax.infants >= pax.adults || pax.adults + pax.children + pax.infants >= 9}
            testid="pax-infants"
          />
        </div>
        <p className="text-[10px] text-[#A09E9A] mt-2 leading-snug">
          Max 9 travelers total · one accompanying adult required per lap infant.
        </p>

        <div className="border-t border-white/10 mt-4 pt-4">
          <p className="tcp-label tcp-label-dark mb-2">Cabin</p>
          <div className="grid grid-cols-2 gap-2">
            {CABINS.map((c) => (
              <button
                key={c.v}
                type="button"
                data-testid={`cabin-${c.v}`}
                onClick={() => setCabin(c.v)}
                className={`px-3 py-2 text-xs tracking-wide transition-colors border ${
                  cabin === c.v
                    ? "bg-[#C29B62] text-[#0A0A0B] border-[#C29B62]"
                    : "text-[#F7F5F0] border-white/15 hover:border-[#C29B62] hover:text-[#C29B62]"
                }`}
              >
                {c.l}
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function FlightSearchWidget({
  initialOrigin = "JFK",
  initialDestination = "LAX",
  initialCabin = "economy",
  initialTripType = "round_trip",
  initialDepart,
  initialReturn,
  initialAdults = 1,
  initialChildren = 0,
  initialInfants = 0,
  onSubmitted,
} = {}) {
  const nav = useNavigate();
  const [tripType, setTripType] = useState(initialTripType);
  const [origin, setOrigin] = useState(initialOrigin);
  const [destination, setDestination] = useState(initialDestination);
  const [depart, setDepart] = useState(
    initialDepart ? new Date(initialDepart + "T00:00:00") : new Date(Date.now() + 21 * 86400000),
  );
  const [ret, setRet] = useState(
    initialReturn ? new Date(initialReturn + "T00:00:00") : new Date(Date.now() + 28 * 86400000),
  );
  const [pax, setPax] = useState({
    adults: Math.max(1, parseInt(initialAdults || 1, 10) || 1),
    children: Math.max(0, parseInt(initialChildren || 0, 10) || 0),
    infants: Math.max(0, parseInt(initialInfants || 0, 10) || 0),
  });
  const [cabin, setCabin] = useState(initialCabin);
  const [airports, setAirports] = useState([]);
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [departOpen, setDepartOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);

  useEffect(() => {
    api
      .get("/products/places/suggestions?q=")
      .then((r) => {
        const list = r.data || [];
        setAirports(list);
        const map = {};
        list.forEach((a) => { map[a.code] = a; });
        setCache((c) => ({ ...map, ...c }));
      })
      .catch(() => setAirports([]));
  }, []);

  const cacheAirport = (airport) => {
    if (airport && airport.code) setCache((c) => ({ ...c, [airport.code]: airport }));
  };
  const selectOrigin = (code, airport) => { cacheAirport(airport); setOrigin(code); };
  const selectDestination = (code, airport) => { cacheAirport(airport); setDestination(code); };

  const swap = () => { setOrigin(destination); setDestination(origin); };

  const submit = (e) => {
    e?.preventDefault?.();
    if (origin === destination) return;
    setLoading(true);
    trackRouteEvent("search", origin, destination);
    const totalPax = (pax.adults || 0) + (pax.children || 0) + (pax.infants || 0);
    const params = new URLSearchParams({
      trip_type: tripType,
      origin,
      destination,
      depart_date: format(depart, "yyyy-MM-dd"),
      ...(tripType === "round_trip" && ret ? { return_date: format(ret, "yyyy-MM-dd") } : {}),
      adults: String(pax.adults),
      children: String(pax.children),
      infants: String(pax.infants),
      passengers: String(totalPax),
      cabin,
    });
    nav(`/search?${params.toString()}`);
    setTimeout(() => setLoading(false), 600);
    // Notify a parent wrapper (e.g. the "Modify search" modal on /search)
    // so it can close itself once the new URL has been pushed.
    if (typeof onSubmitted === "function") onSubmitted();
  };

  const isRoundTrip = tripType === "round_trip";
  const isMobile = useIsMobile();

  // ---------- MOBILE LAYOUT (CheapOair-style stacked card) ----------
  if (isMobile) {
    return (
      <form
        onSubmit={submit}
        className="w-full mx-auto"
        data-testid="flight-search-widget"
      >
        {/* Trip-type segmented control — equal-width buttons, full bleed */}
        <div className="flex items-stretch mb-3 border border-white/10 bg-[#141415]/60">
          {TRIP_TYPES.map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => setTripType(t.v)}
              data-testid={`trip-${t.v}`}
              className={`flex-1 py-2.5 text-[10px] tracking-[0.16em] uppercase font-semibold transition-colors ${
                tripType === t.v
                  ? "bg-[#C29B62] text-[#0A0A0B]"
                  : "text-[#A09E9A] hover:text-[#F7F5F0]"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {/* Stacked input card */}
        <div className="glass-dark divide-y divide-white/10 mb-3">
          {/* From */}
          <div>
            <AirportPicker
              value={origin}
              onChange={selectOrigin}
              label="From"
              defaults={airports}
              cache={cache}
              testid="origin-picker"
              align="start"
            />
          </div>

          {/* To with inline swap button on the right */}
          <div className="relative">
            <AirportPicker
              value={destination}
              onChange={selectDestination}
              label="To"
              defaults={airports}
              cache={cache}
              testid="destination-picker"
              align="start"
            />
            <button
              type="button"
              onClick={swap}
              data-testid="swap-btn"
              aria-label="Swap origin and destination"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#141415] border border-white/15 text-[#C29B62] hover:bg-[#C29B62] hover:text-[#0A0A0B] hover:border-[#C29B62] transition-colors flex items-center justify-center rounded-full"
            >
              <ArrowsLeftRight size={16} weight="bold" />
            </button>
          </div>

          {/* Depart | Return — 50/50 split */}
          <div className="grid grid-cols-2 divide-x divide-white/10">
            <Popover open={departOpen} onOpenChange={setDepartOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="depart-date-btn"
                  className="w-full px-4 py-3 text-left flex flex-col justify-center hover:bg-white/5 transition-colors"
                >
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#A09E9A] font-semibold leading-none mb-1.5">
                    Depart
                  </span>
                  <span className="text-[#F7F5F0] text-sm font-medium leading-tight flex items-center gap-1.5">
                    <CalendarIcon size={13} className="text-[#C29B62] shrink-0" />
                    {depart ? format(depart, "MMM d, yyyy") : "Select"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="p-0 bg-[#141415] border-white/10 text-[#F7F5F0]">
                <Calendar
                  mode="single"
                  selected={depart}
                  onSelect={(d) => {
                    if (!d) return;
                    setDepart(d);
                    setDepartOpen(false);
                    if (isRoundTrip) {
                      setRet((prev) => (!prev || prev < d ? d : prev));
                      setTimeout(() => setReturnOpen(true), 80);
                    }
                  }}
                  disabled={(d) => d < new Date(Date.now() - 86400000)}
                />
              </PopoverContent>
            </Popover>

            <Popover open={returnOpen} onOpenChange={(v) => isRoundTrip && setReturnOpen(v)}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={!isRoundTrip}
                  data-testid="return-date-btn"
                  className={`w-full px-4 py-3 text-left flex flex-col justify-center transition-colors ${
                    isRoundTrip ? "hover:bg-white/5" : "opacity-40 cursor-not-allowed"
                  }`}
                >
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#A09E9A] font-semibold leading-none mb-1.5">
                    Return
                  </span>
                  <span className="text-[#F7F5F0] text-sm font-medium leading-tight flex items-center gap-1.5">
                    <CalendarIcon size={13} className="text-[#C29B62] shrink-0" />
                    {ret && isRoundTrip ? format(ret, "MMM d, yyyy") : "—"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="p-0 bg-[#141415] border-white/10 text-[#F7F5F0]">
                <Calendar
                  mode="single"
                  selected={ret}
                  defaultMonth={ret || depart}
                  onSelect={(d) => {
                    if (!d) return;
                    setRet(d);
                    setReturnOpen(false);
                  }}
                  disabled={(d) => d < depart}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Travelers · Cabin — full-width row */}
          <PaxCabinPopover
            pax={pax}
            setPax={setPax}
            cabin={cabin}
            setCabin={setCabin}
            variant="row"
          />
        </div>

        {/* Big full-width search CTA */}
        <button
          type="submit"
          disabled={loading}
          data-testid="search-flights-btn"
          className="w-full bg-[#C29B62] hover:bg-[#D4AC75] text-[#0A0A0B] font-semibold text-sm tracking-[0.18em] uppercase transition-colors flex items-center justify-center py-4 disabled:opacity-60"
        >
          {loading ? "Searching..." : "Search Flights"}
        </button>
      </form>
    );
  }

  // ---------- DESKTOP LAYOUT (unchanged) ----------
  return (
    <form
      onSubmit={submit}
      className="w-full max-w-6xl mx-auto"
      data-testid="flight-search-widget"
    >
      {/* Top utility row: trip type pills + pax/cabin */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-1">
          {TRIP_TYPES.map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => setTripType(t.v)}
              data-testid={`trip-${t.v}`}
              className={`px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase font-semibold transition-colors ${
                tripType === t.v
                  ? "bg-[#C29B62] text-[#0A0A0B]"
                  : "text-[#A09E9A] hover:text-[#F7F5F0]"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
        <PaxCabinPopover pax={pax} setPax={setPax} cabin={cabin} setCabin={setCabin} />
      </div>

      {/* Main search bar — single sleek horizontal row */}
      <div className="glass-dark flex flex-col md:flex-row items-stretch md:h-[68px] relative">
        {/* From + Swap + To grouped so the swap circle sits exactly between */}
        <div className="flex-[2] flex relative min-w-0">
          <div className="flex-1 min-w-0">
            <AirportPicker
              value={origin}
              onChange={selectOrigin}
              label="From"
              defaults={airports}
              cache={cache}
              testid="origin-picker"
              align="start"
            />
          </div>

          {/* Swap — floating circle exactly between From and To on desktop */}
          <button
            type="button"
            onClick={swap}
            data-testid="swap-btn"
            aria-label="Swap origin and destination"
            className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-[#141415] border border-white/15 text-[#C29B62] hover:bg-[#C29B62] hover:text-[#0A0A0B] hover:border-[#C29B62] transition-colors items-center justify-center rounded-full"
          >
            <ArrowsLeftRight size={15} weight="bold" />
          </button>

          <div className="flex-1 min-w-0 border-t md:border-t-0 md:border-l border-white/10">
            <AirportPicker
              value={destination}
              onChange={selectDestination}
              label="To"
              defaults={airports}
              cache={cache}
              testid="destination-picker"
              align="start"
            />
          </div>
        </div>

        {/* Depart */}
        <div className="md:w-[170px] shrink-0 border-t md:border-t-0 md:border-l border-white/10">
          <Popover open={departOpen} onOpenChange={setDepartOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="depart-date-btn"
                className="w-full h-full px-4 md:px-5 py-2.5 text-left flex flex-col justify-center hover:bg-white/5 transition-colors"
              >
                <span className="text-[10px] tracking-[0.22em] uppercase text-[#A09E9A] font-semibold leading-none mb-1.5">
                  Depart
                </span>
                <span className="text-[#F7F5F0] text-sm font-medium leading-tight flex items-center gap-1.5">
                  <CalendarIcon size={13} className="text-[#C29B62] shrink-0" />
                  {depart ? format(depart, "MMM d, yyyy") : "Select"}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="p-0 bg-[#141415] border-white/10 text-[#F7F5F0]"
            >
              <Calendar
                mode="single"
                selected={depart}
                onSelect={(d) => {
                  if (!d) return;
                  setDepart(d);
                  setDepartOpen(false);
                  // If round-trip, pre-fill the return with the depart date and
                  // immediately surface the return picker so the user can adjust.
                  if (isRoundTrip) {
                    setRet((prev) => (!prev || prev < d ? d : prev));
                    // Tiny delay lets the depart popover close cleanly before
                    // the return popover opens — avoids overlapping focus rings.
                    setTimeout(() => setReturnOpen(true), 80);
                  }
                }}
                disabled={(d) => d < new Date(Date.now() - 86400000)}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Return */}
        <div className="md:w-[170px] shrink-0 border-t md:border-t-0 md:border-l border-white/10">
          <Popover open={returnOpen} onOpenChange={(v) => isRoundTrip && setReturnOpen(v)}>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={!isRoundTrip}
                data-testid="return-date-btn"
                className={`w-full h-full px-4 md:px-5 py-2.5 text-left flex flex-col justify-center transition-colors ${
                  isRoundTrip ? "hover:bg-white/5" : "opacity-40 cursor-not-allowed"
                }`}
              >
                <span className="text-[10px] tracking-[0.22em] uppercase text-[#A09E9A] font-semibold leading-none mb-1.5">
                  Return
                </span>
                <span className="text-[#F7F5F0] text-sm font-medium leading-tight flex items-center gap-1.5">
                  <CalendarIcon size={13} className="text-[#C29B62] shrink-0" />
                  {ret && isRoundTrip ? format(ret, "MMM d, yyyy") : "—"}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="p-0 bg-[#141415] border-white/10 text-[#F7F5F0]"
            >
              <Calendar
                mode="single"
                selected={ret}
                defaultMonth={ret || depart}
                onSelect={(d) => {
                  if (!d) return;
                  setRet(d);
                  setReturnOpen(false);
                }}
                disabled={(d) => d < depart}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Search button */}
        <button
          type="submit"
          disabled={loading}
          data-testid="search-flights-btn"
          className="md:w-[180px] shrink-0 bg-[#C29B62] hover:bg-[#D4AC75] text-[#0A0A0B] font-semibold text-xs tracking-[0.18em] uppercase transition-colors flex items-center justify-center py-4 md:py-0 disabled:opacity-60"
        >
          {loading ? "Searching..." : "Search Flights"}
        </button>
      </div>

      {/* Mobile-only swap button (below the bar) — kept only as fallback
          when the desktop bar wraps; the mobile layout (CheapOair-style)
          renders its own inline swap inside the To row. */}
    </form>
  );
}
